import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Bug, Check, CheckCircle2, Image as ImageIcon, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../components/design/Button';
import { EmptyState } from '../components/design/EmptyState';
import { BugReportModal } from '../components/modals/BugReportModal';
import { userStore } from '../stores/user.store';
import { confirm } from '../stores/confirm.store';
import { apiFetch } from '../utils/api';
import { can } from '@/utils/permissions';

interface BugReport {
    id: number;
    reporter_name: string;
    description: string;
    screenshot_path: string | null;
    status: string;
    created_at: number;
}

const when = (ms: number) =>
    new Date(ms).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * Screenshot mit Login laden: die Route ist geschützt, und <img src> schickt kein
 * Token mit – deshalb per apiFetch als Blob (wie die Server-Icons).
 */
const AuthImage = ({ file, alt, className }: { file: string; alt: string; className?: string }) => {
    const [url, setUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let alive = true;
        let objectUrl: string | null = null;
        setUrl(null);
        setFailed(false);
        apiFetch(`/api/bugs/screenshot/${encodeURIComponent(file)}`)
            .then(async (res) => {
                if (!res.ok) throw new Error(String(res.status));
                const blob = await res.blob();
                if (!alive) return;
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            })
            .catch(() => { if (alive) setFailed(true); });
        return () => {
            alive = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [file]);

    if (failed) return <div className="flex items-center justify-center h-24 text-xs text-fg-muted bg-white/[0.03]">Screenshot nicht verfügbar</div>;
    if (!url) return <div className="h-32 bg-white/[0.04] animate-pulse" />;
    return <img src={url} alt={alt} className={className} />;
};

/** „Fehler melden" (2026-09-15 überarbeitet): deutsch, Screenshots direkt in der Karte. */
export const BugTrackerView = observer(() => {
    const [bugs, setBugs] = useState<BugReport[]>([]);
    const [loading, setLoading] = useState(true);
    const [reportOpen, setReportOpen] = useState(false);
    const [preview, setPreview] = useState<string | null>(null);
    const canResolve = can(userStore.currentUser, 'can_manage_users');

    const fetchBugs = async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/api/bugs');
            const data = await res.json();
            if (data.success) setBugs(data.data);
            else toast.error('Fehlerliste konnte nicht geladen werden');
        } catch {
            toast.error('Fehlerliste konnte nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchBugs(); }, []);

    useEffect(() => {
        if (!preview) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreview(null); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [preview]);

    const resolveBug = async (id: number) => {
        const ok = await confirm({
            title: 'Als erledigt markieren?',
            message: 'Der Eintrag wandert nach „Erledigt".',
            confirmText: 'Erledigt',
            cancelText: 'Abbrechen',
            variant: 'warning',
            icon: 'alert',
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/bugs/${id}`, { method: 'DELETE' });
            if (res.ok) {
                toast.success('Als erledigt markiert');
                setBugs((prev) => prev.map((b) => (b.id === id ? { ...b, status: 'resolved' } : b)));
            } else {
                toast.error('Konnte nicht als erledigt markiert werden');
            }
        } catch {
            toast.error('Keine Verbindung zum Panel');
        }
    };

    const open = bugs.filter((b) => b.status === 'open');
    const resolved = bugs.filter((b) => b.status === 'resolved');

    return (
        <div className="max-w-5xl space-y-6 animate-in fade-in duration-300">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-xl font-semibold text-fg tracking-tight">Fehler melden</h1>
                    <p className="text-sm text-fg-muted mt-1">Etwas funktioniert nicht? Kurz beschreiben, gern mit Screenshot – darunter steht, was schon gemeldet ist.</p>
                </div>
                <Button onClick={() => setReportOpen(true)}>
                    <Bug size={15} /> Fehler melden
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center gap-2 py-16 text-sm text-fg-muted">
                    <Loader2 size={16} className="animate-spin" /> Lade …
                </div>
            ) : bugs.length === 0 ? (
                <EmptyState
                    icon={<CheckCircle2 size={22} />}
                    title="Keine Fehler gemeldet"
                    description="Alles läuft rund. Wenn dir etwas auffällt, melde es oben rechts."
                />
            ) : (
                <>
                    <section>
                        <h2 className="flex items-center gap-2 px-1 mb-2 text-xs font-semibold uppercase tracking-wider text-fg-muted">
                            Offen <span className="px-1.5 rounded-full bg-white/[0.07] text-[11px]">{open.length}</span>
                        </h2>
                        {open.length === 0 ? (
                            <div className="flex items-center gap-2 rounded-2xl border border-line bg-surface px-5 py-5 text-sm text-fg-muted">
                                <CheckCircle2 size={16} className="text-online" /> Nichts offen – alles erledigt.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {open.map((bug) => (
                                    <div key={bug.id} className="bg-surface border border-line rounded-2xl p-5 flex flex-col gap-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="flex items-center gap-2 min-w-0">
                                                <span className="w-6 h-6 shrink-0 rounded-full bg-white/[0.08] text-fg text-[11px] font-semibold flex items-center justify-center">
                                                    {bug.reporter_name.slice(0, 1).toUpperCase()}
                                                </span>
                                                <span className="text-sm font-medium text-fg truncate">{bug.reporter_name}</span>
                                            </span>
                                            <span className="shrink-0 text-xs text-fg-muted tabular-nums">{when(bug.created_at)}</span>
                                        </div>
                                        <p className="flex-1 text-sm text-fg whitespace-pre-wrap break-words">{bug.description}</p>
                                        {bug.screenshot_path && (
                                            <button
                                                type="button"
                                                onClick={() => setPreview(bug.screenshot_path)}
                                                className="block rounded-lg overflow-hidden border border-line hover:border-line-strong transition-colors"
                                                title="Screenshot groß ansehen"
                                            >
                                                <AuthImage file={bug.screenshot_path} alt="Screenshot zum Fehler" className="w-full max-h-48 object-cover object-top" />
                                            </button>
                                        )}
                                        {canResolve && (
                                            <div className="flex justify-end border-t border-line pt-3">
                                                <Button size="sm" variant="secondary" onClick={() => resolveBug(bug.id)}>
                                                    <Check size={14} className="text-online" /> Als erledigt markieren
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {resolved.length > 0 && (
                        <section>
                            <h2 className="px-1 mb-2 text-xs font-semibold uppercase tracking-wider text-fg-muted">Erledigt · die letzten {resolved.length}</h2>
                            <div className="bg-surface border border-line rounded-2xl divide-y divide-line">
                                {resolved.map((bug) => (
                                    <div key={bug.id} className="flex items-start gap-3 px-5 py-3">
                                        <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-online" />
                                        <p className="flex-1 min-w-0 text-sm text-fg-muted line-clamp-2 break-words">{bug.description}</p>
                                        {bug.screenshot_path && (
                                            <button
                                                type="button"
                                                onClick={() => setPreview(bug.screenshot_path)}
                                                aria-label="Screenshot ansehen"
                                                className="shrink-0 p-1 rounded-md text-fg-muted hover:text-fg hover:bg-white/[0.05]"
                                            >
                                                <ImageIcon size={15} />
                                            </button>
                                        )}
                                        <span className="shrink-0 text-xs text-fg-muted hidden sm:inline">
                                            {bug.reporter_name} · {new Date(bug.created_at).toLocaleDateString('de-DE')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}

            <BugReportModal isOpen={reportOpen} onClose={() => { setReportOpen(false); fetchBugs(); }} />

            {preview && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4" onClick={() => setPreview(null)}>
                    <AuthImage file={preview} alt="Screenshot zum Fehler (groß)" className="max-w-full max-h-full rounded-lg shadow-2xl" />
                    <button aria-label="Schließen" className="absolute top-4 right-4 p-2 text-white/80 hover:text-white">
                        <X size={28} />
                    </button>
                </div>
            )}
        </div>
    );
});
